import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { SidebarRowOpinion } from "@remi/contracts/services/person-sidebar-row.service";

import { api } from "~/utils/api";
import { getFavoritePlacementRowId } from "~/components/favorites/favoritePlacement";
import { isSidebarMutation } from "./isSidebarMutation";

/** Forgets where a favorite was dragged. Every favorite toggle calls it, so a favorite starred again
 *  has no stored place and lands on top like a new one, and an unstarred one leaves no row behind. */
export const useClearFavoritePlacement = (): ((favoriteKey: string) => void) => {
  const utils = api.useUtils();
  const queryClient = useQueryClient();

  const { mutate } = api.personSidebarRow.clearRow.useMutation({
    // Cleared before the server answers, or a re-starred row would render at its old place until the refetch.
    onMutate: async ({ rowId }: { rowId: string }) => {
      await utils.personSidebarRow.listMine.cancel();
      const previous = utils.personSidebarRow.listMine.getData();
      if (previous !== undefined) {
        utils.personSidebarRow.listMine.setData(
          undefined,
          previous.filter((row) => row.rowId !== rowId),
        );
      }
      return { previous };
    },
    onError: (_error: unknown, _input: unknown, context: { previous: SidebarRowOpinion[] | undefined } | undefined) => {
      if (context?.previous !== undefined) {
        utils.personSidebarRow.listMine.setData(undefined, context.previous);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ predicate: isSidebarMutation }) <= 1) {
        void utils.personSidebarRow.listMine.invalidate();
      }
    },
  });

  return useCallback((favoriteKey: string) => mutate({ rowId: getFavoritePlacementRowId(favoriteKey) }), [mutate]);
};
